import { useCallback } from 'react';
import { captureSentryError } from './sentry';
import {
  getExtension,
  sendMessageToExtensionIds,
  type ExtensionRuntime,
} from './useExtension.utils';

type BrowserExtensionRuntime = {
  sendMessage: NonNullable<ExtensionRuntime['sendMessage']>;
  lastError?: ReturnType<NonNullable<ExtensionRuntime['getLastError']>>;
};

type BrowserExtensionWindow = Window & {
  chrome?: {
    runtime?: BrowserExtensionRuntime;
  };
};

export const useExtension = () => {
  const { extension } = getExtension(import.meta.env.VITE_PLATFORM_NAME);

  const sendMessage = useCallback(
    async (
      { message, data }: { message: string; data: object },
      callback?: (reply: { exists: boolean }) => void
    ) => {
      try {
        const runtime = (window as BrowserExtensionWindow).chrome?.runtime;

        const delivered = await sendMessageToExtensionIds({
          callback,
          extensionIds: extension.ids,
          message: { message, data },
          runtime: runtime
            ? {
                sendMessage: runtime.sendMessage.bind(runtime),
                getLastError: () => runtime.lastError,
              }
            : undefined,
        });

        if (!delivered.delivered && delivered.reason === 'runtime_error') {
          const details = {
            message,
            extensionIds: extension.ids,
            platform: import.meta.env.VITE_PLATFORM_NAME || 'vrplatform',
            runtimeErrorMessage: delivered.lastErrorMessage,
          };
          captureSentryError(new Error('Extension message delivery failed'), {
            ...details,
          });
        }
      } catch (error: unknown) {
        if (
          error instanceof Error &&
          error.message ===
            'Could not establish connection. Receiving end does not exist.'
        ) {
          // override error when extension is not installed
          return;
        }

        captureSentryError(error);
      }
    },
    [extension.ids]
  );

  return { sendMessage, extension };
};
